import type { PropsWithChildren, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { Page } from 'components/layout/page/Page';

type ContentPaddingSide = 'top' | 'sides' | 'bottom';

type PageLayoutProps = PropsWithChildren<{
  id: string;
  /** The page's PageHeader. Sits above the workspace and does not scroll with the content. */
  header: ReactNode;
  /** Optional in-page secondary navigation (PageSideNav), to the left of the content pane. */
  sideNav?: ReactNode;
  /**
   * Content pane overflow. Defaults to 'auto' (the pane scrolls independently under the header).
   * Pass 'hidden' when the content manages its own internal scrolling/sizing and instead needs a
   * definite height handed down through a flex chain (e.g. a DataGridPro or a split view), rather
   * than the pane's own scrollbar engaging.
   */
  contentOverflow?: 'auto' | 'hidden';
  /**
   * Content pane padding to drop; every side is 24px otherwise. `top` lets a panel meet the header
   * divider, `sides` lets content run edge to edge (e.g. a full-bleed grid), `bottom` lets content that
   * sizes itself (grids, trees) meet the bottom edge or footer.
   */
  disableContentPadding?: ContentPaddingSide[];
  /** Optional bar below the workspace (e.g. a BottomBar). Spans the full page width, outside the page padding. */
  footer?: ReactNode;
}>;

/**
 * The page skeleton for the desktop layout system: a full-width page header above a workspace of
 * optional side navigation plus a scrolling content pane. There is no max content width.
 *
 * The 24px gutters belong to each part rather than to a shared outer box: the header pads itself,
 * the side nav starts at the window's left edge and pads its own items, and the content pane pads *inside* its own scroll area so its
 * scrollbar lands at the right edge of the window.
 */
export function PageLayout({
  id,
  header,
  sideNav,
  contentOverflow = 'auto',
  disableContentPadding = [],
  footer,
  children
}: PageLayoutProps) {
  return (
    <Page id={id} color='white'>
      {header}

      {/*
        `minHeight: 0` is load-bearing: without it this flex child refuses to shrink below its
        content height and the content pane's `overflow: auto` never engages.
      */}
      <Stack direction='row' sx={{ flex: 1, minHeight: 0 }}>
        {sideNav}

        <Box
          data-testid={`${id}-content`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            // Load-bearing for `contentOverflow='hidden'` consumers: without it, a flex-filling
            // child (e.g. a DataGridPro or a split view) can't shrink to receive a definite height
            // from this pane and instead grows to its own content size.
            minHeight: 0,
            overflow: contentOverflow,

            // Reserves the scrollbar's track permanently instead of only when content overflows, so
            // column-count layouts don't silently reflow the moment a scrollbar appears. Only for a
            // scrolling pane: a `hidden` one never shows a scrollbar, and the empty track would leave a
            // strip at the right edge of edge-to-edge content.
            scrollbarGutter: contentOverflow === 'auto' ? 'stable' : undefined,

            pt: disableContentPadding.includes('top') ? 0 : 3,
            px: disableContentPadding.includes('sides') ? 0 : 3,
            pb: disableContentPadding.includes('bottom') ? 0 : 3
          }}
        >
          {children}
        </Box>
      </Stack>

      {footer}
    </Page>
  );
}
